import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, useNavigate } from 'react-router-dom';

import {
  avatarList,
  defaultAvatarHairColor,
  defaultAvatarShirtColor,
  defaultAvatarSkinTone,
  hairColorPalette,
  shirtColorPalette,
  skinColorPalette,
} from 'enrollment/app/constants';
import { ROUTES } from 'enrollment/app/routes';
import { Loader, PageNotFound, RoutesInternal } from 'theSource/components';
import { selectIsAdmin, selectIsAuthenticated } from 'theSource/features';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanYearId,
  setEnrollmentContext,
  setEnrollmentOrgPlanYearId,
  setEnrollmentOrgLifeEventId,
  setEnrollmentOrgPlanId,
  setHasQuestionnaire,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import {
  BenefitCategory,
  EnrollmentContext,
  PlanYear,
  useCompanySettingsQuery,
  useConfigurationQuery,
  useEnrollmentContextQuery,
} from 'theSource/queries';
import { queryClient, queryKeyGenerator } from 'theSource/utils';
import {
  selectEnrollmentLatest,
  setEnrollmentLatest,
  setProfileSteps,
} from './components/BottomNavigation/services';
import {
  INIT_CATEGORIES,
  INIT_PROFILE_STEPS,
} from './components/BottomNavigation/utils';
import {
  selectMaxHeaderItem,
  setIsLoadingBenefits,
  setPerPayPeriodCost,
} from './components/Header/components/EnrollmentNavigation/services';
import {
  BenefitsPreview,
  CommunicationPreferences,
  Family,
  Profile,
  ProfileEdit,
  ESignaturesReview,
  AdditionalPlanInfoReview,
} from './features';
import { AddYourBeneficiaries } from './features/AddYourBeneficiaries/AddYourBeneficiaries';
import { setAllBeneficiaryData } from './features/AddYourBeneficiaries/services';
import { AddYourProviders } from './features/AddYourProviders';
import { BenefitDetails } from './features/BenefitDetails';
import { mapCategoriesData } from './features/Benefits/BenefitsPreview/utils';
import { BeforeGetStarted } from './features/EnrollmentDashboard/components/BeforeGetStarted/BeforeGetStarted';
import { Introduction } from './features/OliveAdvisor';
import { ComparePlans } from './features/Plans/ComparePlans';
import { filteredBeneficiaries } from './features/Plans/PlanCard/utils';
import { PlanSelect } from './features/Plans/PlanSelect';
import {
  chooseAvatarAction,
  selectAvatarHairColorAction,
  selectAvatarShirtColorAction,
  selectAvatarSkinToneAction,
  storeCroppedImage,
} from './features/ProfileEdit/Avatar/AvatarActions';
import { selectedProfileAction } from './features/ProfileEdit/ManageProfile/slice';
import { Questionnaire } from './features/Questionnaire';
import { Review } from './features/Review';
import { useBenefitCategoryQuery, useEnrollmentProfileQuery } from './queries';
import { COVERAGES_BENEFIT_CATEGORY } from './queries/useBenefitCartQuery/utils';
import { setDecisionIQEnabled } from './services';
import { setBenefitCategoryData } from './services/BenefitCategories/slice';
import {
  getLastViewedPage,
  getPageRouter,
  getPlanYears,
  hasOpenLifeEvent,
} from './utils/common/Utils';
import { setHasPreExistingBenefitElections } from './services/CoveragesByCategory';
import { PluginWrapper } from './features/PluginWrapper';
import { setBenefitData } from './services/Benefit/slice';
import { DecisionSupportServices } from './features/DecisionSupportServices';
import { useOliveAdvisorEnabled } from './features/OliveAdvisor/components/OliveAdvisorEnabled';
import {
  hasSavviBenefits,
  useBundledBenefitCategories,
  useSavviOverRideChosenBenefits,
} from './utils/bundledBenefitsUtils';
import { BundledBenefitsHub } from './features/Benefits/BenefitsPreview/BundledBenefitsHub';

export const Enrollment = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const enrollmentLatest = useSelector(selectEnrollmentLatest);
  const maxHeaderItem = useSelector(selectMaxHeaderItem);
  const isAdmin = useSelector(selectIsAdmin);
  const enrollmentSelectedYear = localStorage.getItem('enrollmentSelectedYear');
  const profileRedirectLink = useSelector(
    (state: any) => state?.authentication?.redirectLink
  );
  const savviCategories: BenefitCategory[] = useBundledBenefitCategories();

  const enrollmentContext = useSelector(selectEnrollmentContext);

  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;

  const redirectLink = '/enrollment/profile/edit';

  const { configuration, isLoadingConfiguration } = useConfigurationQuery();
  const { companySettings } = useCompanySettingsQuery({
    enabled: Boolean(configuration?.apiHost),
  });

  const lumosEnabled = companySettings?.data.lumos_feature_toggle || false;
  const { subscriber } = useEnrollmentProfileQuery({ lumosEnabled });

  const profileAvatar = subscriber?.data?.subscriber_profile?.avatar;
  const profilePicture = subscriber?.data?.subscriber_profile?.profile_picture;

  const lumosApiHost = configuration?.apiHost;

  const { enrollmentContext: enrollmentContextResponse } =
    useEnrollmentContextQuery({
      enabled: true,
      apiHost: String(lumosApiHost),
    });
  const oliveAdvisorEnabled = useOliveAdvisorEnabled();

  let queryCategory = '';
  if (
    enrollmentContext !== 'life_event' &&
    INIT_CATEGORIES.includes(enrollmentLatest)
  ) {
    queryCategory = enrollmentLatest;
  }

  let contexts: EnrollmentContext[] = [];
  let activeContextIndex = -1;
  let planYears: PlanYear[] = useMemo(() => [], []);
  if (enrollmentContextResponse) {
    contexts = enrollmentContextResponse?.data as EnrollmentContext[];
    activeContextIndex = Math.max(
      contexts.findIndex((context) => {
        return enrollmentContext === context.enrollment_type_lookup_code;
      }),
      0
    );
    const enrollmentContextData = contexts[activeContextIndex];
    const enrollmentTypeLookup =
      enrollmentContextData.enrollment_type_lookup_code;
    // if user reloads page so context is lost
    if (enrollmentLatest === 'undefined') {
      dispatch(setEnrollmentContext(enrollmentTypeLookup));
      const unfinishedTask = getLastViewedPage({
        currentCategory: contexts[activeContextIndex]?.current_category,
        requiredTask:
          contexts[activeContextIndex].required_tasks[0] || 'confirm',
      });
      dispatch(setEnrollmentLatest(unfinishedTask));
      if (INIT_CATEGORIES.includes(enrollmentLatest)) {
        queryCategory = enrollmentLatest;
      }
      if (
        enrollmentContextData?.subscriber_life_event?.org_plan_year_ids &&
        enrollmentContextData?.subscriber_life_event?.org_plan_year_ids.length >
          1 &&
        enrollmentSelectedYear
      ) {
        dispatch(setEnrollmentOrgPlanYearId(parseInt(enrollmentSelectedYear)));
      }
    }
    planYears = getPlanYears(contexts, activeContextIndex);
  }

  const savviBundledBenefits = useSavviOverRideChosenBenefits();

  const enableBenefitCategoryQuery =
    enrollmentContext === 'initial' ||
    enrollmentContext === 'open' ||
    (enrollmentContext === 'life_event' &&
      hasOpenLifeEvent(enrollmentContextResponse?.data));

  // this loads when you first enter an enrollment context and then never again. DO NOT USE THIS FOR REFRESHING DATA
  // a good use of this is anytime you only care about benefit details but NOT coverage details
  // THIS NO LONGER HAS PLAN LEVEL DATA. DO NOT USE FOR PLAN LEVEL INFORMATION
  const { data: benefitPlans, isLoading: isBenefitsLoading } =
    useBenefitCategoryQuery({
      key: queryKeyGenerator(
        `init-benefits-${enrollmentContext}`,
        orgPlanYearContextId
      ),
      enrollmentContextType: enrollmentContext,
      // category: queryCategory,
      enabled: enableBenefitCategoryQuery && enrollmentLatest !== 'undefined',
      lumosApiHost: String(lumosApiHost),
      includeEligibleDependentsData: true,
      orgPlanYearId: orgPlanYearContextId,
      // getParamCategory: Boolean(queryCategory),
    });

  useEffect(() => {
    const mapBenefitCategoriesData = mapCategoriesData(planYears);
    if (mapBenefitCategoriesData) {
      if (
        savviBundledBenefits &&
        benefitPlans &&
        hasSavviBenefits(
          benefitPlans?.data.benefits,
          benefitPlans?.data.bundled_org_benefit_ids
        )
      ) {
        dispatch(setBenefitCategoryData({ benefitsCategory: savviCategories }));
      } else {
        dispatch(
          setBenefitCategoryData({ benefitsCategory: mapBenefitCategoriesData })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    planYears,
    benefitPlans,
    companySettings?.data.savvi_bundled_benefits_enabled,
    enrollmentContext,
    isAdmin,
  ]);

  useEffect(() => {
    dispatch(setIsLoadingBenefits(isBenefitsLoading));
  }, [isBenefitsLoading, dispatch]);

  useEffect(() => {
    const fetchData = async () => {
      if (enrollmentContext) {
        await queryClient.invalidateQueries(
          queryKeyGenerator(COVERAGES_BENEFIT_CATEGORY, orgPlanYearContextId)
        );
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentContext, benefitPlans, isBenefitsLoading]);

  useEffect(() => {
    if (benefitPlans) {
      // survey benefit types will always have plans data
      const hasSurvey =
        benefitPlans?.data?.benefits?.some(
          (benefit) =>
            benefit?.benefit?.org_benefit_category === 'survey' &&
            benefit?.plans &&
            benefit?.plans?.length > 0 &&
            benefit?.org_benefit_survey_questions &&
            benefit?.org_benefit_survey_questions.length > 0
        ) || false;

      dispatch(setHasQuestionnaire(hasSurvey));
      let initProfileSteps = { ...INIT_PROFILE_STEPS };

      initProfileSteps = {
        ...initProfileSteps,
        questionnaire: hasSurvey ? 'questionnaire' : undefined,
        chatSupport: oliveAdvisorEnabled ? 'olive-advisor' : undefined,
      };

      if (savviBundledBenefits) {
        initProfileSteps = {
          ...initProfileSteps,
          decisionSupport: 'bundles',
        };
      } else if (companySettings?.data.decision_iq_enabled && !isAdmin) {
        initProfileSteps = {
          ...initProfileSteps,
          decisionSupport: 'decisioniq',
        };
      }

      dispatch(setProfileSteps(initProfileSteps));

      // pre-existing benefit elections must be for category later than the current one
      if (enrollmentContext !== 'life_event') {
        let laterBenefitCategories: string[][] = [[], []];
        if (maxHeaderItem === 0) {
          laterBenefitCategories = [['survey'], INIT_CATEGORIES];
        } else if (maxHeaderItem === 1) {
          const benefitCat = INIT_CATEGORIES.findIndex(
            (category) => category === queryCategory
          );
          if (benefitCat !== -1) {
            laterBenefitCategories = [
              [],
              INIT_CATEGORIES.slice(benefitCat + 1),
            ];
          }
        } else if (maxHeaderItem === 2) {
          laterBenefitCategories = [[], []];
        }
        const hasPreExistingBenefit = laterBenefitCategories.map(
          (pageCategories) =>
            benefitPlans.data.benefits.some(
              (benefit) =>
                benefit.coverage !== null &&
                pageCategories.includes(benefit.benefit.org_benefit_category)
            )
        );
        dispatch(setHasPreExistingBenefitElections(hasPreExistingBenefit));
      }
      dispatch(
        setPerPayPeriodCost(
          enrollmentContext === 'life_event' || maxHeaderItem > 0
            ? Number(benefitPlans.data.cart_total || 0)
            : 0
        )
      );
      if (enrollmentContext === 'life_event' || maxHeaderItem > 0) {
        // this is used to calc ppp cost for top right corner
        dispatch(setBenefitData({ benefitsData: benefitPlans }));
      }
    }

    const initialBeneficiaryState = filteredBeneficiaries(benefitPlans)?.filter(
      (beneficiaryData) =>
        beneficiaryData !== null && beneficiaryData !== undefined
    );
    // reset plans state for beneficiaries info. If no beneficiaries, set to empty array to handle dual enrollment scenarios
    if (initialBeneficiaryState?.length) {
      dispatch(setAllBeneficiaryData(initialBeneficiaryState));
    } else {
      dispatch(setAllBeneficiaryData([]));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benefitPlans, dispatch, isBenefitsLoading, enrollmentContext]);

  useEffect(() => {
    const context = enrollmentContextResponse?.data.find(
      (context) => context.enrollment_type_lookup_code === enrollmentContext
    );
    if (enrollmentContext === 'life_event') {
      if (
        context?.subscriber_life_event?.org_plan_year_ids &&
        context?.subscriber_life_event?.org_plan_year_ids.length > 1 &&
        enrollmentSelectedYear
      ) {
        dispatch(setEnrollmentOrgPlanId(parseInt(enrollmentSelectedYear) || 0));
      } else {
        dispatch(
          setEnrollmentOrgPlanId(
            context?.subscriber_life_event?.org_plan_year_ids[0] || 0
          )
        );
      }
      dispatch(
        setEnrollmentOrgLifeEventId(
          context?.subscriber_life_event?.org_life_event_id || 0
        )
      );
      if (
        context?.subscriber_life_event?.org_plan_year_ids &&
        context?.subscriber_life_event?.org_plan_year_ids.length > 1 &&
        enrollmentSelectedYear
      ) {
        dispatch(setEnrollmentOrgPlanYearId(parseInt(enrollmentSelectedYear)));
      }
    } else {
      dispatch(setEnrollmentOrgPlanId(context?.org_plan_year_id || 0));
      dispatch(setEnrollmentOrgLifeEventId(0));
    }
  });

  useEffect(() => {
    if (
      Boolean(profileAvatar) &&
      profileRedirectLink !== '/enrollment/family'
    ) {
      const avatarName = profileAvatar.split('_')[0];
      const avatarPicture = avatarList.filter(
        (item) => profileAvatar && avatarName === item.name
      );
      const avatarSkincolor = skinColorPalette.filter(
        (item) => profileAvatar && profileAvatar.split('_')[1] === item.name
      );
      const avatarHairColor = hairColorPalette.filter(
        (item) => profileAvatar && profileAvatar.split('_')[2] === item.name
      );
      const avatarShirtColor = shirtColorPalette.filter(
        (item) => profileAvatar && profileAvatar.split('_')[3] === item.name
      );

      dispatch(chooseAvatarAction(avatarPicture[0], redirectLink));
      dispatch(
        selectAvatarSkinToneAction(
          avatarSkincolor?.length === 0
            ? {
                id: 'defaultAvatarSkinTone',
                colorCode: defaultAvatarSkinTone,
                name: 'defaultSkinColor',
              }
            : avatarSkincolor[0]
        )
      );
      dispatch(
        selectAvatarHairColorAction(
          avatarHairColor?.length === 0
            ? {
                id: 'defaultAvatarHairColor',
                colorCode: defaultAvatarHairColor,
                name: 'defaultSkinColor',
              }
            : avatarHairColor[0]
        )
      );
      dispatch(
        selectAvatarShirtColorAction(
          avatarShirtColor?.length === 0
            ? {
                id: 'defaultAvatarShirtColor',
                colorCode: defaultAvatarShirtColor,
                name: 'defaultShirtColor',
              }
            : avatarShirtColor[0]
        )
      );
      dispatch(selectedProfileAction(avatarName));
    } else if (
      Boolean(profilePicture) &&
      profileRedirectLink !== '/enrollment/family'
    ) {
      dispatch(storeCroppedImage(profilePicture));
    }
  }, [profileAvatar, profilePicture, dispatch, profileRedirectLink]);

  useEffect(() => {
    if (companySettings) {
      dispatch(
        setDecisionIQEnabled(
          companySettings.data.decision_iq_enabled && !isAdmin
        )
      );
    }
  }, [companySettings, dispatch, isAdmin]);

  const isLoading =
    isLoadingConfiguration ||
    isBenefitsLoading ||
    !configuration ||
    isAuthenticated === null;

  if (isLoading) {
    return <Loader />;
  }

  if (companySettings && !lumosEnabled) {
    return <PageNotFound isFullScreen={true}></PageNotFound>;
  }

  const enrollmentContextData = contexts[activeContextIndex];

  const navTo = (enrollmentPath: string) => {
    navigate(`/enrollment/${enrollmentPath}`);
  };

  const startEnrollmentButton = (context: any) => {
    dispatch(setEnrollmentContext(context.enrollment_type_lookup_code));
    const unfinishedTask = getLastViewedPage({
      currentCategory: context.current_category,
      requiredTask: context.required_tasks[0] || 'confirm',
    });

    dispatch(setEnrollmentLatest(unfinishedTask));

    if (context.status === 'not_started') {
      navTo(`${ROUTES.enrollment.profileEdit.path}`);
      if (Boolean(profilePicture) || Boolean(profileAvatar)) {
        navTo(`${ROUTES.enrollment.profile.path}`);
      } else {
        navTo(`${ROUTES.enrollment.profileEdit.path}`);
      }
    } else {
      if (Boolean(profilePicture) || Boolean(profileAvatar)) {
        navTo(getPageRouter(unfinishedTask));
      } else {
        navTo(`${ROUTES.enrollment.profileEdit.path}`);
      }
    }
  };

  return (
    <RoutesInternal>
      <Route
        path={`${ROUTES.enrollment.profileEdit.path}/*`}
        // element={
        //   !profileAvatar && !profilePicture ? <Dashboard /> : <Profile />
        // }
        element={<ProfileEdit />}
      />
      <Route
        path={ROUTES.enrollment.communicationPreferences.path}
        element={<CommunicationPreferences />}
      />
      <Route
        path={`${ROUTES.enrollment.benefits.path}/*`}
        element={
          savviBundledBenefits ? (
            <BundledBenefitsHub planYears={planYears} />
          ) : (
            <BenefitsPreview planYears={planYears} />
          )
        }
      />
      <Route
        path={`${ROUTES.enrollment.plans.path}/*`}
        element={<PlanSelect />}
      />
      <Route
        path={`${ROUTES.enrollment.comparePlans.path}/*`}
        element={<ComparePlans />}
      />
      <Route
        path={ROUTES.enrollment.questionnaire.path}
        element={<Questionnaire />}
      />
      <Route
        path={`${ROUTES.enrollment.survey.path}/*`}
        element={<Questionnaire />}
      />
      <Route
        path={`${ROUTES.enrollment.profile.path}/*`}
        element={<Profile />}
      />
      <Route path={`${ROUTES.enrollment.family.path}/*`} element={<Family />} />
      <Route
        path={`${ROUTES.enrollment.financialPlans.path}/*`}
        element={<PlanSelect />}
      />
      <Route
        path={`${ROUTES.enrollment.benefitDetails.path}/*`}
        element={<BenefitDetails />}
      />
      <Route
        path={`${ROUTES.enrollment.manageBeneficiaries.path}/*`}
        element={<AddYourBeneficiaries />}
      />
      <Route
        path={`${ROUTES.enrollment.manageProviders.path}/*`}
        element={<AddYourProviders />}
      />
      {/* <Route
        path={`${ROUTES.enrollment.addYourDocuments.path}/*`}
        element={<AddYourDocuments />}
      /> */}
      <Route path={`${ROUTES.enrollment.review.path}/*`} element={<Review />} />
      <Route
        path={`${ROUTES.enrollment.decisionSupportRouter.path}/*`}
        element={<DecisionSupportServices />}
      />
      <Route
        path={ROUTES.enrollment.beforeStarted.path}
        element={
          <BeforeGetStarted
            startEnrollmentButton={startEnrollmentButton}
            context={enrollmentContextData}
          />
        }
      />
      <Route
        path={ROUTES.enrollment.oliveAdvisor.path}
        element={<Introduction />}
      />
      <Route
        path={`${ROUTES.enrollment.plugin.path}/*`}
        element={<PluginWrapper />}
      />
      <Route
        path={`${ROUTES.enrollment.eSign.path}/*`}
        element={<ESignaturesReview />}
      />
      <Route
        path={`${ROUTES.enrollment.AdditionalPlanInfo.path}`}
        element={<AdditionalPlanInfoReview />}
      />
    </RoutesInternal>
  );
};
